import React, { useState, useEffect } from 'react';
import { DateTime } from 'luxon';
import Icon from '@mdi/react';
import { mdiClose, mdiPencil, mdiArrowRight, mdiChevronDown, mdiChevronUp } from '@mdi/js';
import { toast } from 'react-toastify';
import { availabilityApi } from '../api/availability';
import DateInputWithPicker from './DateInputWithPicker';
import CustomFormInput from './forms/CustomFormInput';
import WeeklyDayRows, { DAY_NAMES, buildWindows, windowsToDayConfig } from './WeeklyDayRows';
import './WeeklyScheduleModal.css'; // Reuse same styles

/**
 * EditWeeklyScheduleModal - Edit an existing recurring schedule period
 *
 * Same layout as WeeklyScheduleModal (create): per-day availability rows with
 * slot groups + breaks (WeeklyDayRows), lesson duration pills, and the
 * schedule-period accordion. Prefills via windowsToDayConfig — each stored
 * window becomes a slot group on its day (a persisted break is a gap between
 * windows, so it round-trips as two groups; identical windows on save).
 *
 * Saves the full per-day config as windows[] via
 * PUT /api/v2/availability/recurring/:id (single call — one doc per schedule
 * period). The old version read only windows[0] and rebuilt every window with
 * one global time, which destroyed per-day config on save.
 *
 * Props:
 *   isOpen      - boolean
 *   schedule    - schedule period doc from ManageAvailabilityUnified:
 *                 { id, scheduleName, validFrom, validUntil, windows[], defaultSlotDurationMins }
 *   onSaved     - called after successful save (reloads calendar)
 *   onCancel    - called on cancel/close
 */
const EditWeeklyScheduleModal = ({ isOpen, schedule, onSaved, onCancel }) => {
    const [scheduleName, setScheduleName] = useState('');
    const [dayConfig, setDayConfig] = useState(() => windowsToDayConfig([]));

    // Date range — endDate is the single source of truth; numWeeks stays in sync.
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [numWeeks, setNumWeeks] = useState(8);

    // Lesson duration (minutes per bookable slot)
    const [duration, setDuration] = useState(60);

    // UI state
    const [isSaving, setIsSaving] = useState(false);
    // Dates already exist on an edit — the schedule-period accordion starts
    // collapsed, showing the from → to summary.
    const [dateAccordionOpen, setDateAccordionOpen] = useState(false);

    // Weeks between two ISO dates, rounded to the nearest whole week (min 1).
    const weeksBetween = (fromISO, toISO) => {
        const days = DateTime.fromISO(toISO).diff(DateTime.fromISO(fromISO), 'days').days;
        return Math.max(1, Math.round(days / 7));
    };

    // Editing duration → recompute end date from start + weeks.
    const handleWeeksChange = (weeks) => {
        const safeWeeks = Math.max(1, weeks || 1);
        setNumWeeks(safeWeeks);
        if (startDate) {
            setEndDate(DateTime.fromISO(startDate).plus({ weeks: safeWeeks }).toISODate());
        }
    };

    // Editing end date → recompute duration (rounded to nearest week).
    const handleEndDateChange = (newEndDate) => {
        setEndDate(newEndDate);
        if (startDate && newEndDate) {
            setNumWeeks(weeksBetween(startDate, newEndDate));
        }
    };

    // Editing start date → keep end date anchored to the current duration.
    const handleStartDateChange = (newStartDate) => {
        setStartDate(newStartDate);
        if (newStartDate && numWeeks) {
            setEndDate(DateTime.fromISO(newStartDate).plus({ weeks: numWeeks }).toISODate());
        }
    };

    const computedEndDate = endDate || null;

    // Pre-populate from the schedule period doc when the modal opens
    useEffect(() => {
        if (isOpen && schedule) {
            setScheduleName(schedule.scheduleName || '');
            setDayConfig(windowsToDayConfig(schedule.windows || []));
            const from = schedule.validFrom ? DateTime.fromISO(schedule.validFrom).toISODate() : '';
            const until = schedule.validUntil ? DateTime.fromISO(schedule.validUntil).toISODate() : '';
            setStartDate(from);
            setEndDate(until);
            if (from && until) setNumWeeks(weeksBetween(from, until));
            setDuration(schedule.defaultSlotDurationMins || 60);
            setDateAccordionOpen(false);
            setIsSaving(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, schedule?.id]);

    const handleSave = async () => {
        const selectedDayNames = DAY_NAMES.filter(day => dayConfig[day].enabled);
        if (selectedDayNames.length === 0) {
            toast.error('Please select at least one day');
            return;
        }
        if (!startDate) {
            toast.error('Please set a start date');
            return;
        }
        if (!computedEndDate) {
            toast.error('Please set an end date');
            return;
        }
        const start = DateTime.fromISO(startDate);
        const end = DateTime.fromISO(computedEndDate);
        if (!start.isValid || !end.isValid || end <= start) {
            toast.error('End date must be after start date');
            return;
        }

        const windows = buildWindows(dayConfig);
        if (windows.length === 0) {
            toast.error('Your breaks cover all the available time. Adjust the times or remove a break.');
            return;
        }

        setIsSaving(true);

        try {
            const updates = {
                scheduleName: scheduleName.trim() || schedule.scheduleName,
                validFrom: startDate,
                validUntil: computedEndDate,
                defaultSlotDurationMins: duration,
                windows,
                // Breaks are baked into windows[] as gaps — the legacy
                // schedule-wide lunchBreak field stays disabled.
                lunchBreak: { enabled: false },
            };

            await availabilityApi.updateRecurringSchedule(schedule.id, updates);

            toast.success(`Schedule "${updates.scheduleName}" updated`);
            onSaved();
        } catch (error) {
            console.error('Error updating schedule:', error);
            toast.error(error.message || 'Failed to update schedule');
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen || !schedule) return null;

    return (
        <div className="weekly-schedule-modal-overlay" onClick={onCancel}>
            <div className="weekly-schedule-modal" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="weekly-schedule-modal-header">
                    <div className="weekly-schedule-modal-header-content">
                        <Icon path={mdiPencil} size={1.2} color="var(--dl-color-turquoise-default)" />
                        <h2>Edit Schedule</h2>
                    </div>
                    <button className="weekly-schedule-modal-close" onClick={onCancel}>
                        <Icon path={mdiClose} size={1} />
                    </button>
                </div>

                {/* Body */}
                <div className="weekly-schedule-modal-body">
                    {/* Schedule Name */}
                    <div className="form-group">
                        <label htmlFor="editScheduleName" className="form-label">Schedule Name</label>
                        <input
                            type="text"
                            id="editScheduleName"
                            className="form-input"
                            value={scheduleName}
                            onChange={e => setScheduleName(e.target.value)}
                            maxLength={100}
                        />
                    </div>

                    {/* Lesson Duration — label + pills share one row */}
                    <div className="form-group weekly-lesson-duration">
                        <label className="form-label">Lesson Duration</label>
                        <div className="duration-radio">
                            {[45, 60, 90, 120].map(d => (
                                <CustomFormInput
                                    key={d}
                                    type="radio"
                                    variant="turquoise"
                                    layout="pill"
                                    name="editDuration"
                                    value={String(d)}
                                    label={`${d} min`}
                                    checked={duration === d}
                                    onChange={() => setDuration(d)}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Schedule period — accordion. Collapsed header shows the
                        from → to dates; expanded reveals Start date / Duration /
                        End date. */}
                    <div className="form-group weekly-section-divided">
                        <label className="form-label">Schedule period</label>
                        <div className={`date-accordion${dateAccordionOpen ? ' open' : ''}`}>
                            <button
                                type="button"
                                className="date-accordion-header"
                                onClick={() => setDateAccordionOpen(o => !o)}
                                aria-expanded={dateAccordionOpen}>
                                <span className="date-accordion-summary">
                                    <span className="date-accordion-value">
                                        {startDate
                                            ? DateTime.fromISO(startDate).toFormat('EEE d LLL yy')
                                            : 'Start date'}
                                    </span>
                                    <Icon path={mdiArrowRight} size={0.7} className="date-accordion-arrow" color="var(--dl-color-gray-500)" />
                                    <span className="date-accordion-value">
                                        {computedEndDate
                                            ? DateTime.fromISO(computedEndDate).toFormat('EEE d LLL yy')
                                            : 'End date'}
                                    </span>
                                </span>
                                <Icon
                                    path={dateAccordionOpen ? mdiChevronUp : mdiChevronDown}
                                    size={0.9}
                                    color="var(--dl-color-purple-default)"
                                />
                            </button>

                            {dateAccordionOpen && (
                                <div className="date-accordion-body">
                                    <div className="date-duration-row">
                                        <div className="form-group">
                                            <label className="form-label">Start date</label>
                                            <DateInputWithPicker
                                                value={startDate}
                                                onChange={handleStartDateChange}
                                            />
                                        </div>
                                        <Icon path={mdiArrowRight} size={0.9} className="date-duration-arrow" color="var(--dl-color-turquoise-hover)" />
                                        <div className="duration-block">
                                            <span className="form-label">Duration</span>
                                            <div className="duration-weeks-row">
                                                <input
                                                    type="number"
                                                    min="1"
                                                    value={numWeeks}
                                                    onChange={(e) => handleWeeksChange(parseInt(e.target.value))}
                                                    className="weeks-number-input"
                                                    disabled={!startDate}
                                                />
                                                <span className="weeks-suffix">weeks</span>
                                            </div>
                                        </div>
                                        <Icon path={mdiArrowRight} size={0.9} className="date-duration-arrow" color="var(--dl-color-turquoise-hover)" />
                                        <div className="form-group">
                                            <label className="form-label">End date</label>
                                            <DateInputWithPicker
                                                value={endDate}
                                                onChange={handleEndDateChange}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Weekly availability — one row per day, shared with the create modal */}
                    <div className="form-group">
                        <label className="form-label">General availability</label>
                        <WeeklyDayRows dayConfig={dayConfig} setDayConfig={setDayConfig} duration={duration} />
                    </div>
                </div>

                {/* Footer */}
                <div className="weekly-schedule-modal-footer">
                    <button className="btn-cancel" onClick={onCancel} disabled={isSaving}>
                        Cancel
                    </button>
                    <button className="btn-primary" onClick={handleSave} disabled={isSaving}>
                        {isSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EditWeeklyScheduleModal;
